import styled from '@emotion/styled';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { color, font } from 'src/shared/styles/variables';

interface Props {
    lastRating?: number;
    ratingId: string;
    title: string;
    subtitle: string;
    onSelect: (ratingId: string, value: number) => void;
}

interface RatingButtonProps {
    parentHasSelection: boolean;
    selected: boolean;
}

interface RatingDivProps {
    hasSelection: boolean;
}

const OuterDiv = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    marginBottom: 24,
});

const Title = styled('h3')(font.heavy(font.size.medium));

const Subtitle = styled('h4')(font.medium(font.size.basePlus));

const RatingButtonsDiv = styled('div')<RatingDivProps>(({ hasSelection }) => ({
    display: 'flex',
    border: `2px solid ${color.catskillWhite}`,
    borderRadius: 24,
    backgroundColor: hasSelection ? color.royalBlue : color.coconut,
    justifyContent: 'space-around',
}));

const RatingButton = styled('button')(
    {
        border: 'none',
        boxShadow: 'none',
        width: '20%',
        textAlign: 'center',
        backgroundColor: 'transparent',
        '&:hover': {
            border: 'none',
            boxShadow: 'none',
        },
        '&:focus': {
            border: 'none',
            boxShadow: 'none',
        },
    },
    font.heavy(font.size.mediumExtended),
);

const RatingTextValue = styled('span')<RatingButtonProps>(({ selected, parentHasSelection }) => ({
    display: 'inline-block',
    width: 25,
    borderRadius: '100%',
    color: selected ? color.black : parentHasSelection ? color.white : color.blueBayoux,
    backgroundColor: selected ? color.white : 'transparent',
}));

const RatingDescriptors = styled('div')({
    display: 'flex',
    justifyContent: 'space-between',
});

const ImportanceText = styled('span')(font.book(font.size.base));

export const Rating1to5: React.FC<Props> = ({
    ratingId,
    lastRating,
    title,
    subtitle,
    onSelect,
}) => {
    const [rating, setRating] = useState(lastRating || 0);
    const { t } = useTranslation();

    const handleRating = (value: number) => evt => {
        evt.preventDefault();
        evt.stopPropagation();
        setRating(value);
        onSelect(ratingId, value);
    };

    return (
        <OuterDiv>
            <Title>{t(title)}</Title>
            <Subtitle>{t(subtitle)}</Subtitle>
            <RatingButtonsDiv hasSelection={!!rating}>
                {[...Array(5)].map((_, idx) => {
                    const value = idx + 1;
                    return (
                        <RatingButton
                            value={value}
                            key={`${ratingId}_${value}`}
                            onClick={handleRating(value)}
                        >
                            <RatingTextValue
                                selected={rating === value}
                                parentHasSelection={!!rating}
                            >
                                {value}
                            </RatingTextValue>
                        </RatingButton>
                    );
                })}
            </RatingButtonsDiv>
            <RatingDescriptors>
                <ImportanceText>{t('intakeForms:numerical_rating.least_important')}</ImportanceText>
                <ImportanceText>{t('intakeForms:numerical_rating.most_important')}</ImportanceText>
            </RatingDescriptors>
        </OuterDiv>
    );
};
